import { inject, Injectable } from '@angular/core';
import { AgentCategory } from '../models/agent-config.model';
import { AiClientService } from '../services/ai-client.service';
import { AiConversation, AiConversationItem, TokenUsage } from '../transports/ai-transport';
import { CacheMeta, SummaryConversationCacheService } from '../services/summary-conversation-cache.service';
import { DB_KEY_PREFIXES } from '@omni/config/pouch-db.config';
import { unfenceJson } from '../transports/json-fence';
import { CardResponse, parseCardResponse } from './card.schema';
import { RecordKind, RecordRef } from './record-ref';
import { AiDataSourceRegistry } from '../summary/data-source-registry.service';

/** Agent category for the AI Summary card of a kind. */
export function summaryCategoryFor(kind: RecordKind): AgentCategory {
  switch (kind) {
    case 'contact': return AgentCategory.ContactSummary;
    case 'account': return AgentCategory.AccountSummary;
    case 'spotlight': return AgentCategory.SpotlightSummary;
  }
}

/**
 * Brief **trigger** (not the prompt itself). Deliberately policy-free: content rules
 * (factual-only, tone, structure) live in the brief agent's defaults and can be reshaped
 * per deployment via indskr_additionalinstructions (injected as a system message). Any
 * policy wording here would compete with those instructions on every call.
 */
const SUMMARY_BRIEF_PROMPT = 'Provide the summary brief.';

/** Deterministic JSON: object keys sorted at every level so equal data always yields equal text. */
function stableStringify(value: unknown): string {
  return JSON.stringify(value, (_k, v) =>
    v && typeof v === 'object' && !Array.isArray(v)
      ? Object.keys(v).sort().reduce((o, k) => { (o as Record<string, unknown>)[k] = (v as Record<string, unknown>)[k]; return o; }, {} as Record<string, unknown>)
      : v,
  );
}

/** Compact, stable string hash (FNV-1a) for change detection — not for security. */
function hashString(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16);
}

/** Lift the first known string field from a thin JSON wrapper — `{text}`/`{summary}`/`{content}`. */
function liftWrappedProse(raw: string): string {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) return '';
  try {
    const parsed = JSON.parse(trimmed) as Record<string, unknown>;
    for (const key of ['text', 'summary', 'content', 'output']) {
      if (typeof parsed[key] === 'string') return (parsed[key] as string).trim();
    }
  } catch {
    // Not JSON — the agent's prose is the summary.
  }
  return trimmed;
}

/**
 * Normalise an agent reply into a `CardResponse`. The summary agents return a card_response
 * envelope (currently a single `text` section); a reply that is plain prose or a `{text}`
 * wrapper is lifted into one `text` section, so the surface reads one model regardless of the
 * agent's exact output and can carry richer sections later without a contract change.
 */
function toCardResponse(raw: string): CardResponse {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) return { title: null, sections: [], actions: null };
  try {
    const parsed = JSON.parse(unfenceJson(trimmed)) as Record<string, unknown>;
    if (parsed && typeof parsed === 'object' && Array.isArray(parsed['sections'])) {
      return parseCardResponse(parsed);
    }
  } catch {
    // Not card_response JSON — fall through to prose.
  }
  const prose = liftWrappedProse(trimmed);
  return { title: null, sections: prose ? [{ type: 'text', text: prose }] : [], actions: null };
}

/**
 * AI Summary card surface: a `CardResponse` (currently a single factual-prose `text` section)
 * from the per-kind summary agents (ContactSummary/AccountSummary/SpotlightSummary). Holds its
 * own conversation so it never clobbers the Nova Insights thread for the same record.
 */
/** The card plus when it was generated (ISO), so the surface can show "generated X ago". */
export interface BriefResult {
  card: CardResponse;
  generatedAt: string | null;
  /** Responses API id of the turn that produced this card, for feedback correlation. On a cache
   *  hit this is the ORIGINAL generation's id (a hit produces no new turn). */
  responseId?: string;
  /** Token usage for the turn that produced this card. Same cache-hit carry-forward as above. */
  usage?: TokenUsage;
}

@Injectable({ providedIn: 'root' })
export class RecordBriefFeature {
  #briefConversation: AiConversation | null = null;
  readonly #ai = inject(AiClientService);
  readonly #cache = inject(SummaryConversationCacheService);
  readonly #registry = inject(AiDataSourceRegistry);

  /** Whether a summary agent (AI Summary card) is configured for this kind. */
  isAvailable(kind: RecordKind): boolean {
    return this.#ai.isAvailable(summaryCategoryFor(kind));
  }

  /**
   * Disk cache key for a record's brief — keyed by kind + id only (no prompt/config hash).
   * The `brief_` discriminator scopes it to this arm, so it stays distinct from any other
   * surface keying the same RECORD_SUMMARY prefix by (kind, id).
   */
  #cacheKey(kind: RecordKind, id: string): string {
    return DB_KEY_PREFIXES.RECORD_SUMMARY + 'brief_' + kind + '_' + (id || 'spotlight');
  }

  /** The raw agent reply cached for this brief (newest assistant turn). */
  #peelCached(items: AiConversationItem[] | null): string | null {
    if (!items?.length) return null;
    for (let i = items.length - 1; i >= 0; i--) {
      const text = items[i].text?.trim();
      if (items[i].role === 'assistant' && text) return text;
    }
    return null;
  }

  /**
   * The record's AI summary as a `CardResponse` from its dedicated summary agent, plus when it
   * was generated.
   *
   * Cache-first, but the cache is only served when the grounding still matches: each reply is
   * stored with a signature of the grounding it was generated from, and a cached reply is reused
   * only when the record's current grounding signature is unchanged. If the grounding or inputs
   * have changed since, the card auto-regenerates on open — no manual refresh needed. The rep can
   * still force a fresh copy explicitly via `force: true` (the card's regenerate button).
   *
   * Grounding is assembled by the transport from the agent's config + this recordId; the feature
   * recomputes the same signature locally to decide whether the cache is still current.
   */
  async brief(record: RecordRef, opts?: { force?: boolean }): Promise<BriefResult> {
    const category = summaryCategoryFor(record.kind);
    const cacheKey = this.#cacheKey(record.kind, record.id ?? '');
    const signature = await this.#groundingSignature(category, record);

    // Serve the cache only when its grounding signature still matches the record's current
    // grounding (convId omitted → local-only read, never a Foundry round-trip).
    if (!opts?.force) {
      const meta = await this.#cache.getCacheMeta(cacheKey);
      if (meta.signature === signature) {
        const hit = await this.#readCached(category, cacheKey, meta);
        if (hit) return hit;
      }
    }

    const previous = this.#briefConversation;
    const conv = await this.#ai.startConversation(category);

    // The transport assembles + attaches grounding (and the rep's session/identity context);
    // recordId scopes it, recordType/origin are this surface's own call metadata.
    conv.setStructuredInputs({
      recordId: record.id ?? '',
      recordType: record.kind,
      origin: 'goapp-summary-card',
    });

    const input = [{ type: 'message', role: 'user', content: SUMMARY_BRIEF_PROMPT }];

    const raw = (await conv.send(input)) ?? '';
    const card = toCardResponse(raw);
    if (!card.sections.length) {
      throw new Error('Agent returned no summary content');
    }

    this.#briefConversation = conv;
    const lastTurn = conv.lastTurn;
    // Persist the raw reply with the signature it was generated from (so the next open can tell
    // whether it is still current) and the feedback metadata (so a later cache hit can still show
    // the original generation's token usage and take a vote).
    await this.#cache.cacheItems(cacheKey, conv.id, [{ id: '', role: 'assistant', text: raw }], {
      signature,
      responseId: lastTurn?.responseId,
      usage: lastTurn?.usage,
    });
    if (previous && previous.id !== conv.id) {
      await previous.delete().catch(() => undefined); // discard the superseded brief
    }
    return { card, generatedAt: new Date().toISOString(), responseId: lastTurn?.responseId, usage: lastTurn?.usage };
  }

  /**
   * The brief persisted on device for this record, or null when none has been generated yet.
   * Reads local storage only — never calls the agent — so this is the card's source while the rep
   * is offline. The grounding signature is deliberately not consulted: offline there is no way to
   * regenerate, so a brief whose grounding has since drifted still beats showing nothing.
   */
  async cachedBrief(record: RecordRef): Promise<BriefResult | null> {
    const category = summaryCategoryFor(record.kind);
    const cacheKey = this.#cacheKey(record.kind, record.id ?? '');
    return this.#readCached(category, cacheKey, await this.#cache.getCacheMeta(cacheKey));
  }

  /** The stored reply under a key as a `BriefResult`, or null when it holds no usable card. */
  async #readCached(category: AgentCategory, cacheKey: string, meta: CacheMeta): Promise<BriefResult | null> {
    const cached = this.#peelCached(await this.#cache.resolve(category, null, cacheKey));
    if (!cached) return null;
    const card = toCardResponse(cached);
    return card.sections.length
      ? { card, generatedAt: meta.at, responseId: meta.responseId ?? undefined, usage: meta.usage ?? undefined }
      : null;
  }

  /**
   * A stable fingerprint of everything the summary is grounded in: the record's structured inputs
   * plus the assembled grounding forest (the same sources the transport sends). Re-opening the card
   * recomputes this cheaply from on-device data; a changed fingerprint means the cached reply is
   * stale and the card regenerates.
   */
  async #groundingSignature(category: AgentCategory, record: RecordRef): Promise<string> {
    let sections: unknown = null;
    try {
      sections = await this.#registry.resolve(category, { recordId: record.id ?? '' });
    } catch {
      sections = null;
    }
    const basis = { recordId: record.id ?? '', recordType: record.kind, grounding: sections };
    return hashString(stableStringify(basis));
  }

  /** Releases the local handle without deleting server-side. */
  async endConversation(): Promise<void> {
    this.#briefConversation = null;
  }
}
